<script lang="ts" setup>
import { Label, SwitchRoot, SwitchThumb } from "reka-ui"
import { useField } from "vee-validate"
import { toRef } from "vue"
import { Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<SwitchProps>(), {
  variant: "default",
  color: "default",
})

const emit = defineEmits<{
  "update:modelValue": [value: boolean]
  "change": [value: boolean]
}>()

const { t } = useTranslation()

type ISwitchVariant = "default" | "text" | "icon"
type ISwitchColor = "default" | "themed"

interface SwitchProps {
  name: string
  label?: string
  modelValue?: boolean
  error?: string
  disabled?: boolean
  hint?: string
  isLoading?: boolean
  variant?: ISwitchVariant
  color?: ISwitchColor
}

const name = toRef(props, "name")
const { value, errorMessage, handleChange } = useField<boolean>(name)

function onInput(value: boolean) {
  handleChange(value)
  emit("update:modelValue", value)
  emit("change", value)
}
</script>

<template>
  <div class="flex gap-2 items-center">
    <Label
      :for="name"
      class="flex gap-2 items-center"
      :class="{ 'opacity-60 cursor-not-allowed': disabled }"
      data-test="switch_label"
    >
      <SwitchRoot
        :id="name"
        :name="name"
        data-test="switch"
        class="group relative flex shrink-0 cursor-pointer border-stone-300 transition-[background] data-[state=unchecked]:bg-stone-300 focus-within:outline-hidden focus-within:shadow-[0_0_0_1px] data-disabled:cursor-not-allowed data-disabled:bg-stone-400 data-disabled:border-stone-400 data-disabled:shadow-none data-disabled:data-[state=checked]:bg-stone-400 data-disabled:data-[state=checked]:border-stone-400"
        :class="{
          'w-8 h-5 border shadow-xs rounded-full': variant === 'default',
          'w-[120px] h-[40px] border-y-2 border-x-4 rounded-lg': variant === 'text' || variant === 'icon',
          'data-[state=checked]:bg-[#072a47] data-[state=checked]:border-[#072a47] focus-within:border-[#072a47] focus-within:shadow-[#072a47]': color === 'default',
          'data-[state=checked]:bg-primary data-[state=checked]:border-primary focus-within:border-primary focus-within:shadow-primary': color === 'themed',
        }"
        :model-value="value || modelValue"
        :disabled="disabled"
        @update:model-value="onInput"
      >
        <SwitchThumb
          class="my-auto flex items-center justify-center bg-white text-xs shadow-xl transition-transform will-change-transform data-[state=checked]:translate-x-full group-data-disabled:bg-stone-100 group-data-disabled:shadow-none"
          :class="{
            'w-3.5 h-3.5 translate-x-0.5 rounded-full': variant === 'default',
            'w-1/2 h-[34px] rounded-lg': variant === 'text' || variant === 'icon',
          }"
        >
          <span v-if="variant === 'text'">{{ (value || modelValue) ? t('yes') : t('no') }}</span>
          <Icon
            v-if="variant === 'icon'"
            class="pointer-events-none"
            :icon="(value || modelValue) ? 'CircleCheck' : 'CircleRemove'"
          />
        </SwitchThumb>
      </SwitchRoot>
      <span>{{ label }}</span>
    </Label>
    <Icon v-if="isLoading" data-test="switch_loading_icon" icon="Loading" />
    <span v-if="error || errorMessage" data-test="switch_error" class="text-red-600 text-sm">
      {{ error || errorMessage }}
    </span>
    <span v-if="hint" data-test="switch_hint" class="text-sm">{{ hint }}</span>
  </div>
</template>
